import React, { useEffect, useState } from 'react'
import { courseService } from '@/services/courseService'
import { apiClient } from '@/services/api'

export interface SessionFormData {
    sessionNumber: number
    title: string
    description: string
    dateTime: string
    durationMinutes: number
    location: string
    meetingLink: string
    type: 'theory' | 'practical'
}

export interface BatchFormData {
    courseId: string
    title: string
    description: string
    scheduleType: string
    startDate: string
    endDate: string
    registrationDeadline: string
    location: string
    meetingLink: string
    capacity: number
    minimumParticipants: number
    batchFee: number
    trainerId: string
    defaultRoomCapacity: number
    photoDriveUrl: string
    photoDriveDescription: string
    sessions: SessionFormData[]
}

interface BatchFormProps {
    onSubmit: (data: BatchFormData) => void
    onCancel: () => void
    isLoading?: boolean
    initialData?: BatchFormData
}

const DRIVE_URL_REGEX = /^https?:\/\/(?:[a-z0-9-]+\.)?drive\.google\.com\/.*/i

const emptySession = (num: number): SessionFormData => ({
    sessionNumber: num,
    title: '',
    description: '',
    dateTime: '',
    durationMinutes: 120,
    location: '',
    meetingLink: '',
    type: 'practical',
})

export const BatchForm: React.FC<BatchFormProps> = ({ onSubmit, onCancel, isLoading, initialData }) => {
    const [courses, setCourses] = useState<{ id: string; title: string }[]>([])
    const [instructors, setInstructors] = useState<{ id: string; fullName: string }[]>([])
    const [formData, setFormData] = useState<BatchFormData>(initialData || {
        courseId: '',
        title: '',
        description: '',
        scheduleType: 'weekly',
        startDate: '',
        endDate: '',
        registrationDeadline: '',
        location: '',
        meetingLink: '',
        trainerId: '',
        capacity: 30,
        minimumParticipants: 1,
        batchFee: 0,
        defaultRoomCapacity: 2,
        photoDriveUrl: '',
        photoDriveDescription: '',
        sessions: [],
    })
    const [driveUrlError, setDriveUrlError] = useState<string | null>(null)
    const [coursesError, setCoursesError] = useState<string | null>(null)

    useEffect(() => {
        setCoursesError(null)
        courseService.getCourses().then((res) => {
            if (res.data) setCourses(res.data as unknown as { id: string; title: string }[])
        }).catch((err: unknown) => {
            console.error('Failed to load courses:', err)
            setCoursesError('Gagal memuat daftar kursus. Silakan coba lagi.')
        })
    }, [])

    useEffect(() => {
        if (!formData.courseId) { setInstructors([]); return }
        apiClient.get<any>(`/courses/${formData.courseId}/instructors`).then((res: any) => {
            const splits = res?.data ?? res ?? []
            setInstructors(Array.isArray(splits) ? splits.map((s: any) => ({ id: s.instructorId, fullName: s.instructorName || s.fullName || s.name || s.instructorEmail || s.instructorId })) : [])
        }).catch(() => setInstructors([]))
    }, [formData.courseId])

    const handleDriveUrlChange = (value: string) => {
        setFormData(prev => ({ ...prev, photoDriveUrl: value }))
        if (value && !DRIVE_URL_REGEX.test(value)) {
            setDriveUrlError('Harus URL Google Drive yang valid')
        } else {
            setDriveUrlError(null)
        }
    }

    const addSession = () => {
        setFormData(prev => ({
            ...prev,
            sessions: [...prev.sessions, emptySession(prev.sessions.length + 1)],
        }))
    }

    const removeSession = (index: number) => {
        setFormData(prev => ({
            ...prev,
            sessions: prev.sessions
                .filter((_, i) => i !== index)
                .map((s, i) => ({ ...s, sessionNumber: i + 1 })),
        }))
    }

    const updateSession = (index: number, field: keyof SessionFormData, value: string | number) => {
        setFormData(prev => ({
            ...prev,
            sessions: prev.sessions.map((s, i) =>
                i === index ? { ...s, [field]: value } : s
            ),
        }))
    }

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        if (formData.photoDriveUrl && !DRIVE_URL_REGEX.test(formData.photoDriveUrl)) {
            setDriveUrlError('Harus URL Google Drive yang valid')
            return
        }
        onSubmit(formData)
    }

    const inputClass = "w-full pl-11 pr-4 py-3 bg-slate-50 dark:bg-white/[0.03] border border-slate-200 dark:border-white/10 focus:border-primary/50 focus:ring-4 focus:ring-primary/5 rounded-xl text-sm font-bold text-slate-900 dark:text-white transition-all outline-none placeholder:text-slate-400 placeholder:font-medium shadow-sm"
    const inputNoIconClass = "w-full px-4 py-3 bg-slate-50 dark:bg-white/[0.03] border border-slate-200 dark:border-white/10 focus:border-primary/50 focus:ring-4 focus:ring-primary/5 rounded-xl text-sm font-bold text-slate-900 dark:text-white transition-all outline-none placeholder:text-slate-400 placeholder:font-medium shadow-sm"
    const labelClass = "block text-sm font-black text-slate-500 dark:text-slate-400 mb-1.5 ml-1"

    return (
        <form onSubmit={handleSubmit} className="space-y-5 animate-in fade-in slide-in-from-bottom-2 duration-500">
            {/* Course & Title */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-1">
                    <label className={labelClass}>Program Kursus <span className="text-primary">*</span></label>
                    <div className="relative group">
                        <span className="absolute left-3.5 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-400 text-[20px] transition-colors group-focus-within:text-primary">school</span>
                        <select
                            value={formData.courseId}
                            onChange={(e) => setFormData(prev => ({ ...prev, courseId: e.target.value }))}
                            className={`${inputClass} appearance-none cursor-pointer`}
                            required
                            disabled={!!coursesError}
                        >
                            <option value="">{coursesError ? 'Gagal memuat kursus' : 'Pilih course...'}</option>
                            {courses.map((c) => (
                                <option key={c.id} value={c.id}>{c.title}</option>
                            ))}
                        </select>
                        <span className="absolute right-3.5 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-400 pointer-events-none">expand_more</span>
                    </div>
                    {coursesError && (
                        <div className="flex items-center gap-2 mt-1 ml-1">
                            <span className="text-[11px] font-bold text-rose-500">{coursesError}</span>
                            <button type="button" onClick={() => {
                                setCoursesError(null)
                                courseService.getCourses().then((res) => {
                                    if (res.data) setCourses(res.data as unknown as { id: string; title: string }[])
                                }).catch(() => setCoursesError('Gagal memuat daftar kursus. Silakan coba lagi.'))
                            }} className="text-[11px] font-bold text-primary underline hover:opacity-70">
                                Coba lagi
                            </button>
                        </div>
                    )}
                </div>
                <div className="space-y-1">
                    <label className={labelClass}>Nama Batch <span className="text-primary">*</span></label>
                    <div className="relative group">
                        <span className="absolute left-3.5 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-400 text-[20px] transition-colors group-focus-within:text-primary">label</span>
                        <input type="text" value={formData.title}
                            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                            placeholder="Contoh: Batch Mei 2026" className={inputClass} required />
                    </div>
                </div>
            </div>

            {/* Instructor */}
            <div className="space-y-1">
                <label className={labelClass}>Instruktur Pengampu</label>
                <div className="relative group">
                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-400 text-[20px] transition-colors group-focus-within:text-primary">person</span>
                    <select
                        value={formData.trainerId}
                        onChange={(e) => setFormData(prev => ({ ...prev, trainerId: e.target.value }))}
                        className={`${inputClass} appearance-none cursor-pointer`}
                        disabled={!formData.courseId}
                    >
                        <option value="">{formData.courseId ? 'Pilih instruktur...' : 'Pilih course dulu'}</option>
                        {instructors.map((i) => (
                            <option key={i.id} value={i.id}>{i.fullName}</option>
                        ))}
                    </select>
                    <span className="absolute right-3.5 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-400 pointer-events-none">expand_more</span>
                </div>
            </div>

            {/* Description */}
            <div className="space-y-1">
                <label className={labelClass}>Deskripsi Singkat</label>
                <div className="relative group">
                    <span className="absolute left-3.5 top-3.5 material-symbols-outlined text-slate-400 text-[20px] transition-colors group-focus-within:text-primary">description</span>
                    <textarea value={formData.description}
                        onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                        rows={2} className={`${inputClass} pl-11 resize-none`}
                        placeholder="Berikan gambaran singkat batch ini..." />
                </div>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <div className="space-y-1">
                    <label className={labelClass}>Tipe Jadwal</label>
                    <div className="relative group">
                        <span className="absolute left-3.5 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-400 text-[20px] transition-colors group-focus-within:text-primary">event_repeat</span>
                        <select value={formData.scheduleType}
                            onChange={(e) => setFormData(prev => ({ ...prev, scheduleType: e.target.value }))}
                            className={`${inputClass} appearance-none cursor-pointer`} required>
                            <option value="weekly">Weekly</option>
                            <option value="custom">Custom</option>
                        </select>
                        <span className="absolute right-3.5 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-400 pointer-events-none">expand_more</span>
                    </div>
                </div>
                <div className="space-y-1">
                    <label className={labelClass}>Mulai <span className="text-primary">*</span></label>
                    <div className="relative group">
                        <span className="absolute left-3.5 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-400 text-[20px] transition-colors group-focus-within:text-primary">calendar_month</span>
                        <input type="date" value={formData.startDate}
                            onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
                            className={inputClass + " [color-scheme:light] dark:[color-scheme:dark]"} required />
                    </div>
                </div>
                <div className="space-y-1">
                    <label className={labelClass}>Selesai <span className="text-primary">*</span></label>
                    <div className="relative group">
                        <span className="absolute left-3.5 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-400 text-[20px] transition-colors group-focus-within:text-primary">event_busy</span>
                        <input type="date" value={formData.endDate}
                            onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
                            className={inputClass + " [color-scheme:light] dark:[color-scheme:dark]"} required />
                    </div>
                </div>
            </div>

            {/* Registration Deadline */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-1">
                    <label className={labelClass}>Batas Pendaftaran</label>
                    <div className="relative group">
                        <span className="absolute left-3.5 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-400 text-[20px] transition-colors group-focus-within:text-primary">event_available</span>
                        <input type="date" value={formData.registrationDeadline}
                            onChange={(e) => setFormData(prev => ({ ...prev, registrationDeadline: e.target.value }))}
                            className={inputClass + " [color-scheme:light] dark:[color-scheme:dark]"} />
                    </div>
                    <p className="text-[10px] text-slate-400 ml-1">Kosongkan jika tidak ada batas pendaftaran.</p>
                </div>
            </div>

            {/* Location & Meeting Link */}
            <div className="space-y-4 p-5 bg-slate-100/50 dark:bg-white/[0.02] rounded-2xl border border-slate-200 dark:border-white/5">
                <p className="text-xs font-black text-slate-500 dark:text-slate-400 uppercase tracking-wider">Lokasi Default Batch</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                        <label className={labelClass}>Lokasi Offline</label>
                        <div className="relative group">
                            <span className="absolute left-3.5 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-400 text-[20px] transition-colors group-focus-within:text-primary">location_on</span>
                            <input type="text" value={formData.location}
                                onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                                placeholder="Contoh: Balai Pelatihan, Jl. Raya No.1, Jakarta"
                                className={inputClass} />
                        </div>
                    </div>
                    <div className="space-y-1">
                        <label className={labelClass}>Link Meeting Online <span className="text-slate-400 font-medium normal-case">(opsional)</span></label>
                        <div className="relative group">
                            <span className="absolute left-3.5 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-400 text-[20px] transition-colors group-focus-within:text-primary">videocam</span>
                            <input type="url" value={formData.meetingLink}
                                onChange={(e) => setFormData(prev => ({ ...prev, meetingLink: e.target.value }))}
                                placeholder="https://meet.google.com/..."
                                className={inputClass} />
                        </div>
                    </div>
                </div>
            </div>

            {/* Capacity & Fees */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 p-5 bg-slate-100/50 dark:bg-white/[0.02] rounded-2xl border border-slate-200 dark:border-white/5">
                <div className="space-y-1">
                    <label className={labelClass}>Kapasitas <span className="text-primary">*</span></label>
                    <div className="relative group">
                        <span className="absolute left-3.5 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-400 text-[20px] transition-colors group-focus-within:text-primary">groups</span>
                        <input type="number" min={1} value={formData.capacity}
                            onChange={(e) => setFormData(prev => ({ ...prev, capacity: parseInt(e.target.value) || 1 }))}
                            className={inputClass} required />
                    </div>
                </div>
                <div className="space-y-1">
                    <label className={labelClass}>Min. Peserta</label>
                    <div className="relative group">
                        <span className="absolute left-3.5 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-400 text-[20px] transition-colors group-focus-within:text-primary">person_check</span>
                        <input type="number" min={1} value={formData.minimumParticipants}
                            onChange={(e) => setFormData(prev => ({ ...prev, minimumParticipants: parseInt(e.target.value) || 1 }))}
                            className={inputClass} />
                    </div>
                </div>
                <div className="space-y-1">
                    <label className={labelClass}>Biaya (IDR)</label>
                    <div className="relative group">
                        <span className="absolute left-3.5 top-1/2 -translate-y-1/2 font-black text-slate-400 text-xs transition-colors group-focus-within:text-primary">Rp</span>
                        <input type="number" min={0} value={formData.batchFee}
                            onChange={(e) => setFormData(prev => ({ ...prev, batchFee: parseInt(e.target.value) || 0 }))}
                            className={inputClass} />
                    </div>
                </div>
            </div>

            {/* Sessions */}
            {!initialData ? (
                <div className="space-y-4 p-5 bg-slate-100/50 dark:bg-white/[0.02] rounded-2xl border border-slate-200 dark:border-white/5">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-xs font-black text-slate-500 dark:text-slate-400 uppercase tracking-wider">Jadwal Sesi Pertemuan</p>
                            <p className="text-[10px] text-slate-400 mt-0.5">Tiap sesi adalah 1 pertemuan dengan tanggal & lokasi spesifik.</p>
                        </div>
                        <button
                            type="button"
                            onClick={addSession}
                            className="flex items-center gap-1.5 px-4 py-2 bg-primary text-white rounded-xl text-xs font-black hover:opacity-90 transition-all shadow-md shadow-primary/20"
                        >
                            <span className="material-symbols-outlined text-[16px]">add</span>
                            Tambah Sesi
                        </button>
                    </div>

                    {formData.sessions.length === 0 ? (
                        <div className="text-center py-8 border-2 border-dashed border-slate-200 dark:border-white/10 rounded-xl">
                            <span className="material-symbols-outlined text-3xl text-slate-300 dark:text-slate-600 block mb-2">calendar_add_on</span>
                            <p className="text-sm font-bold text-slate-400">Belum ada sesi</p>
                            <p className="text-xs text-slate-400 mt-0.5">Klik "Tambah Sesi" untuk menambahkan jadwal pertemuan perdana</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {formData.sessions.map((session, index) => (
                                <div key={index} className="p-4 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-white/10 space-y-3">
                                    {/* Session header */}
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <span className="w-7 h-7 rounded-lg bg-primary/10 text-primary text-xs font-black flex items-center justify-center">
                                                {session.sessionNumber}
                                            </span>
                                            <span className="text-sm font-black text-slate-700 dark:text-slate-200">Sesi {session.sessionNumber}</span>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => removeSession(index)}
                                            className="p-1.5 rounded-lg text-slate-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-500/10 transition-all"
                                        >
                                            <span className="material-symbols-outlined text-[18px]">delete</span>
                                        </button>
                                    </div>

                                    {/* Title & Type */}
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                        <div className="md:col-span-2">
                                            <label className={labelClass}>Judul Sesi <span className="text-primary">*</span></label>
                                            <input type="text" value={session.title}
                                                onChange={(e) => updateSession(index, 'title', e.target.value)}
                                                placeholder="Contoh: Orientasi & Pengenalan"
                                                className={inputNoIconClass} required />
                                        </div>
                                        <div>
                                            <label className={labelClass}>Tipe</label>
                                            <select value={session.type}
                                                onChange={(e) => updateSession(index, 'type', e.target.value)}
                                                className={`${inputNoIconClass} appearance-none cursor-pointer`}>
                                                <option value="theory">Teori</option>
                                                <option value="practical">Praktik</option>
                                            </select>
                                        </div>
                                    </div>

                                    {/* DateTime & Duration */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        <div>
                                            <label className={labelClass}>Tanggal & Jam <span className="text-primary">*</span></label>
                                            <input type="datetime-local" value={session.dateTime}
                                                onChange={(e) => updateSession(index, 'dateTime', e.target.value)}
                                                className={inputNoIconClass + " [color-scheme:light] dark:[color-scheme:dark]"} required />
                                        </div>
                                        <div>
                                            <label className={labelClass}>Durasi (menit)</label>
                                            <input type="number" min={30} step={30} value={session.durationMinutes}
                                                onChange={(e) => updateSession(index, 'durationMinutes', parseInt(e.target.value) || 120)}
                                                className={inputNoIconClass} />
                                        </div>
                                    </div>

                                    {/* Location & Meeting Link */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        <div>
                                            <label className={labelClass}>Lokasi Sesi <span className="text-slate-400 font-medium normal-case">(opsional)</span></label>
                                            <input type="text" value={session.location}
                                                onChange={(e) => updateSession(index, 'location', e.target.value)}
                                                placeholder="Kosongkan jika sama dengan lokasi batch"
                                                className={inputNoIconClass} />
                                        </div>
                                        <div>
                                            <label className={labelClass}>Link Meeting <span className="text-slate-400 font-medium normal-case">(opsional)</span></label>
                                            <input type="url" value={session.meetingLink}
                                                onChange={(e) => updateSession(index, 'meetingLink', e.target.value)}
                                                placeholder="https://meet.google.com/..."
                                                className={inputNoIconClass} />
                                        </div>
                                    </div>

                                    {/* Description */}
                                    <div>
                                        <label className={labelClass}>Deskripsi Sesi</label>
                                        <textarea value={session.description}
                                            onChange={(e) => updateSession(index, 'description', e.target.value)}
                                            rows={2} placeholder="Apa yang akan dipelajari di sesi ini..."
                                            className={`${inputNoIconClass} resize-none`} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            ) : (
                <div className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-500/10 rounded-2xl border border-blue-200 dark:border-blue-500/20">
                    <span className="material-symbols-outlined text-blue-500 mt-0.5">info</span>
                    <div>
                        <p className="text-sm font-bold text-blue-900 dark:text-blue-400">Kelola Jadwal Sesi Pertemuan</p>
                        <p className="text-xs text-blue-700 dark:text-blue-300/80 mt-1 leading-relaxed">
                            Pengelolaan jadwal sesi pertemuan (tambah, edit, hapus) untuk batch ini kini dilakukan secara terpisah untuk pengalaman yang lebih baik. Silakan simpan perubahan form ini, lalu buka tab <strong>"Kelola Kamar & Jadwal"</strong> pada halaman detail batch.
                        </p>
                    </div>
                </div>
            )}

            {/* Drive Link */}
            <div className="space-y-4 p-5 bg-slate-100/50 dark:bg-white/[0.02] rounded-2xl border border-slate-200 dark:border-white/5">
                <div className="space-y-1">
                    <label className={labelClass}>Link Foto Dokumentasi (G-Drive)</label>
                    <div className="relative group">
                        <span className="absolute left-3.5 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-400 text-[20px] transition-colors group-focus-within:text-primary">cloud_upload</span>
                        <input type="url" value={formData.photoDriveUrl}
                            onChange={(e) => handleDriveUrlChange(e.target.value)}
                            placeholder="https://drive.google.com/drive/folders/..."
                            className={inputClass} aria-label="Google Drive Photo URL" />
                    </div>
                    {driveUrlError && (
                        <p className="mt-1 text-[10px] font-bold text-rose-500 ml-1 uppercase tracking-widest" role="alert">{driveUrlError}</p>
                    )}
                </div>
                <div className="space-y-1">
                    <label className={labelClass}>Deskripsi Link</label>
                    <div className="relative group">
                        <span className="absolute left-3.5 top-3.5 material-symbols-outlined text-slate-400 text-[20px] transition-colors group-focus-within:text-primary">comment</span>
                        <textarea value={formData.photoDriveDescription}
                            onChange={(e) => setFormData(prev => ({ ...prev, photoDriveDescription: e.target.value }))}
                            rows={2} placeholder="Contoh: Foto dokumentasi sesi praktik batch ini"
                            className={`${inputClass} pl-11 resize-none`} aria-label="Drive Description" />
                    </div>
                </div>
            </div>

            {/* Room Capacity */}
            <div className="space-y-1">
                <label className={labelClass}>Kapasitas Kamar <span className="text-slate-400 font-medium normal-case tracking-normal">(untuk event hybrid)</span></label>
                <div className="relative group">
                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-400 text-[20px] transition-colors group-focus-within:text-primary">meeting_room</span>
                    <input type="number" min={1} value={formData.defaultRoomCapacity}
                        onChange={(e) => setFormData(prev => ({ ...prev, defaultRoomCapacity: Math.max(1, parseInt(e.target.value) || 1) }))}
                        className={inputClass} aria-label="Default Room Capacity" />
                </div>
                <p className="text-[10px] text-slate-400 ml-1">Jumlah peserta per kamar saat auto-assign (default 2).</p>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-6 border-t border-slate-100 dark:border-white/5">
                <button type="button" onClick={onCancel}
                    className="px-6 py-3 text-slate-500 dark:text-slate-400 text-sm font-bold hover:text-slate-900 dark:hover:text-white transition-all">
                    Batal
                </button>
                <button type="submit" disabled={isLoading || !!driveUrlError}
                    className="px-8 py-3.5 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-xl text-sm font-bold hover:scale-[1.02] active:scale-[0.98] transition-all shadow-xl shadow-slate-200 dark:shadow-none disabled:opacity-50">
                    {isLoading ? 'Processing...' : 'Simpan Batch'}
                </button>
            </div>
        </form>
    )
}
